import { ChangeDetectorRef, Directive, Input } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { defer, finalize, Observable, take, timeout } from 'rxjs';

/**
 * Common plumbing shared by the admin panel's section components
 * (Manage Environments, Manage Users, Manage Ads, Manage Alerts, Special Events):
 * per-action busy tracking, generic client-side pagination, and toast/error helpers.
 *
 * Extracted from the original monolithic AdminPanelComponent so each section can
 * live in its own component while keeping identical behavior.
 */
@Directive()
export abstract class AdminSectionBaseComponent {
  @Input() token = '';

  loading = false;
  protected readonly apiCallTimeoutMs = 30000;
  private pendingRequestCount = 0;
  private readonly actionPendingCount = new Map<string, number>();
  private readonly pagination = new Map<string, { page: number; pageSize: number }>();

  constructor(protected snackBar: MatSnackBar, protected cdr: ChangeDetectorRef) {}

  protected initPagination(key: string, pageSize: number): void {
    this.pagination.set(key, { page: 1, pageSize });
  }

  private paginationState(key: string): { page: number; pageSize: number } {
    let state = this.pagination.get(key);
    if (!state) {
      state = { page: 1, pageSize: 10 };
      this.pagination.set(key, state);
    }
    return state;
  }

  getCurrentPage(key: string): number {
    return this.paginationState(key).page;
  }

  getTotalPages(key: string, totalItems: number): number {
    const pageSize = this.paginationState(key).pageSize;
    return Math.max(1, Math.ceil(totalItems / pageSize));
  }

  getPaginatedItems<T>(key: string, items: T[]): T[] {
    const state = this.paginationState(key);
    const totalPages = this.getTotalPages(key, items.length);
    state.page = Math.min(Math.max(1, state.page), totalPages);
    const start = (state.page - 1) * state.pageSize;
    return items.slice(start, start + state.pageSize);
  }

  resetPage(key: string): void {
    this.paginationState(key).page = 1;
  }

  goToPreviousPage(key: string): void {
    const state = this.paginationState(key);
    state.page = Math.max(1, state.page - 1);
  }

  goToNextPage(key: string, totalItems: number): void {
    const state = this.paginationState(key);
    const totalPages = this.getTotalPages(key, totalItems);
    state.page = Math.min(totalPages, state.page + 1);
  }

  isActionBusy(key: string): boolean {
    return (this.actionPendingCount.get(key) || 0) > 0;
  }

  protected trackApiCall<T>(request$: Observable<T>): Observable<T> {
    return defer(() => {
      this.pendingRequestCount += 1;
      this.loading = true;
      return request$.pipe(take(1), timeout(this.apiCallTimeoutMs));
    }).pipe(
      finalize(() => this.applyState(() => {
        this.pendingRequestCount = Math.max(0, this.pendingRequestCount - 1);
        this.loading = this.pendingRequestCount > 0;
      })),
    );
  }

  protected trackActionCall<T>(actionKey: string, request$: Observable<T>, timeoutMs: number = this.apiCallTimeoutMs): Observable<T> {
    return defer(() => {
      this.incrementAction(actionKey);
      return request$.pipe(take(1), timeout(timeoutMs));
    }).pipe(
      finalize(() => this.applyState(() => this.decrementAction(actionKey))),
    );
  }

  /**
   * Applies a state mutation and forces a change-detection pass on this view.
   * Async work in this app can complete outside the Angular zone (AngularFire),
   * and ApplicationRef.tick() only refreshes dirty-marked views, so we detect
   * changes on this component directly to reliably repaint after these updates.
   */
  protected applyState(mutate: () => void): void {
    mutate();
    try {
      this.cdr.detectChanges();
    } catch {
      /* detectChanges() throws if called re-entrantly during an existing CD pass; safe to ignore. */
    }
  }

  private incrementAction(actionKey: string): void {
    const currentCount = this.actionPendingCount.get(actionKey) || 0;
    this.actionPendingCount.set(actionKey, currentCount + 1);
  }

  private decrementAction(actionKey: string): void {
    const currentCount = this.actionPendingCount.get(actionKey) || 0;
    if (currentCount <= 1) {
      this.actionPendingCount.delete(actionKey);
      return;
    }

    this.actionPendingCount.set(actionKey, currentCount - 1);
  }

  protected toNullableString(value: any): string | null {
    if (value === undefined || value === null || String(value).trim() === '') {
      return null;
    }
    return String(value).trim();
  }

  protected toNullableNumber(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  protected showSuccess(message: string): void {
    this.snackBar.open(message, 'OK');
  }

  protected showError(message: string): void {
    this.snackBar.open(message, 'OK');
  }

  protected handleError(error: any): void {
    const message = error?.error?.error || error?.error?.message || error?.message || 'Request failed.';
    this.showError(message);
  }
}
